<template>
  <div v-show="showCarousel" class="product-carousel critical-component-clear">
    <div class="product-carousel__container">
      <div class="product-carousel__header">
        <div class="product-carousel__header-text">
          <h2
            v-if="liquid.sectionTitle"
            class="product-carousel__title text-mobile-headlines-h3 text-desktop-headline-h3"
            v-text="liquid.sectionTitle"
          />

          <p
            v-if="liquid.sectionSubtitle"
            class="text-mobile-paragraph-m-regular text-desktop-paragraph-m-regular"
            v-text="liquid.sectionSubtitle"
          />
        </div>

        <btn
          v-if="collection"
          class="product-carousel__cta text-mobile-paragraph-m-regular-link text-desktop-paragraph-m-regular-link"
          modifiers="text"
          size="small-small"
          :url="collection.handle"
          :label="collection.title"
        />
      </div>

      <div
        v-if="liquid.sectionType === 'carousel'"
        class="product-carousel__splide splide"
        :js-section-id="liquid.sectionId"
      >
        <div class="splide__track carousel">
          <div class="product-carousel__list carousel splide__list">
            <div
              v-for="(product, index) of products"
              :key="index"
              :class="itemClass"
            >
              <product-card
                :is-variant="isVariant"
                :product="product"
                :variant="product.selectedOrFirstAvailableVariant"
                location="Section Product Carousel"
              />
            </div>
          </div>
        </div>

        <div class="splide__arrows">
          <button class="splide__arrow splide__arrow--prev" type="button">
            <span
              class="visually-hidden"
              v-text="$string('accessibility.carousel.previous')"
            />

            <icon-arrow-left />
          </button>

          <button class="splide__arrow splide__arrow--next" type="button">
            <span
              class="visually-hidden"
              v-text="$string('accessibility.carousel.next')"
            />

            <icon-arrow-right />
          </button>
        </div>
      </div>

      <div v-else class="product-carousel__splide">
        <div class="product-carousel__list grid">
          <div
            v-for="(product, index) of products"
            :key="index"
            :class="itemClass"
          >
            <product-card
              :is-variant="isVariant"
              :lazyload="index > 4"
              :product="product"
              :variant="product.selectedOrFirstAvailableVariant"
              location="Section Product Carousel"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Vue: Product carousel (product-carousel)
 * -----------------------------------------------------------------------------
 * A multipurpose product carousel.
 *
 * @param {String} liquid.sectionId - Section's Liquid ID.
 * @param {String} liquid.sectionType Product Section's type.
 * @param {String} liquid.sectionTitle - Section setting title.
 * @param {String} liquid.productId - Product ID.
 * @param {String} liquid.source - Product source.
 * @param {String} liquid.metaobject - Metaobject handle referencing products.
 * @param {Array}  liquid.lastOrderedProducts - Last ordered products.
 * @param {Number} liquid.limit - Product limit.
 *
 */
import { Splide } from '@splidejs/splide';
import { mapState } from 'pinia';

import Btn from '~global/btn/btn';
import ProductCard from '~global/product-card/product-card';

import IconArrowLeft from '~icons/directional-navigation/arrow/left.svg';
import IconArrowRight from '~icons/directional-navigation/arrow/right.svg';

import recommendedProductsQuery from '~graphql/recommended-products.gql';
import productCarouselQuery from '~graphql/product-carousel.gql';
import nodesByIdQuery from '~graphql/nodes-by-id-product-card.gql';

import { formatCarouselProducts } from '~/format/format-carousel-products';
import { formatTrackingItem } from '~/format/format-tracking-item';

import { transformId } from '~/helpers/graphql';
import { computeDiscountLogic } from '~/helpers/vue';
import timings from '~/config/timings';

import { values as breakpoints } from '~/config/breakpoints';
import useLastViewedProductsStore from '~stores/last-viewed-products';
import useDiscountCampaignsStore from '~stores/discount-campaigns';
import useIndexStore from '~stores/index';

export default {
  name: 'ProductCarousel',

  components: {
    Btn,
    IconArrowLeft,
    IconArrowRight,
    ProductCard,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        lastOrderedProducts: [],
        limit: 12,
        metaobject: '',
        placeholderCount: 8,
        productId: '',
        sectionId: '',
        sectionSubtitle: '',
        sectionTitle: '',
        sectionType: '',
        source: '',
        hideSoldout: false,
      }),
    },
  },

  data() {
    return {
      carousel: null,
      carouselRef: null,
      collection: null,
      isVariant: false,
      products: Array.from(
        { length: this.liquid.placeholderCount },
        () => false,
      ),
      state: 'loading',
    };
  },

  computed: {
    ...mapState(useIndexStore, {
      isMobile: (store) => store.screen.mobile,
    }),
    ...mapState(useDiscountCampaignsStore, {
      getCampaignDiscount: 'getCampaignDiscount',
      discountCode: 'code',
    }),
    ...mapState(useLastViewedProductsStore, {
      viewedProducts: (state) => state.products,
    }),

    /**
     * Map Pinia state.
     */

    ...mapState(useLastViewedProductsStore, {
      viewedProducts: (state) => state.products,
    }),

    /**
     * Compute product item class.
     * @returns {String}
     */
    itemClass() {
      const baseClass = 'product-carousel__product';

      if (this.liquid.sectionType === 'carousel') {
        return `${baseClass} splide__slide`;
      }

      return `${baseClass} col m2 l3`;
    },

    /**
     * Compute query type.
     * @returns {String}
     */
    queryType() {
      if (this.liquid.source === 'recommendations') {
        return recommendedProductsQuery;
      }

      if (
        (this.liquid.source === 'last-ordered' &&
          this.liquid.lastOrderedProducts.length >= 1) ||
        (this.liquid.source === 'last-viewed' &&
          this.viewedProducts.length >= 4)
      ) {
        return nodesByIdQuery;
      }

      return productCarouselQuery;
    },

    /**
     * Compute query variables.
     * @returns {Object}
     */
    queryVariables() {
      const { source, productId, limit, metaobject, lastOrderedProducts } =
        this.liquid;

      if (source === 'recommendations' && productId) {
        return {
          country: this.$variable('store.country'),
          intent: 'RELATED',
          language: this.$variable('store.language'),
          productId: transformId(productId, 'Product'),
        };
      }

      if (source === 'last-viewed' && this.viewedProducts.length >= 4) {
        return {
          country: this.$variable('store.country'),
          ids: this.viewedProducts.map((id) => transformId(id, 'Product')),
          language: this.$variable('store.language'),
        };
      }

      if (
        source === 'last-ordered' &&
        this.$variable('customer.loggedIn') &&
        lastOrderedProducts.length >= 1
      ) {
        return {
          country: this.$variable('store.country'),
          ids: lastOrderedProducts
            .slice(0, limit)
            .map((id) => transformId(id, 'ProductVariant')),
          language: this.$variable('store.language'),
        };
      }

      return {
        country: this.$variable('store.country'),
        handle: {
          handle: metaobject,
          type: 'product_variant_list',
        },
        language: this.$variable('store.language'),
        limit,
      };
    },

    /**
     * Compute whether to show carousel.
     * - Hide 'last-seen' carousel by default.
     * @returns {Boolean}
     */
    showCarousel() {
      if (this.state === 'loading' && this.liquid.source === 'last-viewed') {
        return false;
      }

      return this.products.length > 0;
    },
  },

  mounted() {
    const { source, lastOrderedProducts } = this.liquid;

    if (
      (source === 'last-ordered' && lastOrderedProducts.length < 1) ||
      (source === 'last-viewed' && this.viewedProducts.length < 4)
    ) {
      this.products = [];
      return;
    }

    this.carouselRef = `#vue-element [js-section-id="${this.liquid.sectionId}"]`;
    this.fetchProducts();
  },

  methods: {
    /**
     * Construct the carousel.
     */
    constructCarousel() {
      if (!this.carouselRef) {
        return;
      }

      this.carousel = new Splide(this.carouselRef, {
        arrows: false,
        drag: true,
        focus: false,
        gap: 'var(--layout-gutter)',
        mediaQuery: 'min',
        omitEnd: true,
        pagination: false,
        perMove: 2,
        perPage: 2,
        slideFocus: false,
        snap: true,
        speed: this.$timing('normal'),
        type: 'slide',
        breakpoints: {
          [breakpoints.l]: {
            arrows: true,
            perPage: 4,
            drag: false,
          },
        },
      });

      setTimeout(() => {
        this.carousel.mount();
      }, timings.normal);
    },

    /**
     * Fetch products based on source.
     */
    async fetchProducts() {
      const response = await cnvs.Query({
        query: this.queryType,
        variables: this.queryVariables,
      });

      const { products, collection, isVariant } = formatCarouselProducts(
        response,
        this.liquid.limit,
      );

      if (this.$variable('settings.disableComponentReadyState')) {
        return;
      }

      this.products = products.filter((product) => {
        const availableState = product.selectedOrFirstAvailableVariant
          ? product.selectedOrFirstAvailableVariant.availableState
          : product.availableState;
        return (
          product.availableState !== 'hidden' &&
          !(this.liquid.hideSoldout && availableState !== 'available') &&
          `${product?.variants[0]?.id}` !==
            this.$variable('product.depositProductID') &&
          product.hiddenProduct?.value !== 'true'
        );
      });

      if (collection) {
        this.collection = collection;
      }

      if (isVariant) {
        this.isVariant = isVariant;
      }

      // Track product list view
      this.trackProductListView();

      this.state = 'ready';

      /** isMobile is necessary to properly calculate the width of section */
      if (!this.isMobile && this.liquid.sectionType === 'carousel') {
        await this.$nextTick();
        this.constructCarousel();
      }
    },

    /**
     * Track product list view
     * - Need to send collection title, id and the product list
     */
    trackProductListView() {
      cnvs.EventBus.emit('dataLayer:view_item_list', {
        collection:
          this.collection?.handle ||
          this.liquid.sectionTitle ||
          this.liquid.source ||
          '-',
        collectionId: this.collection?.id || this.liquid.id || '-',
        products: this.products.map((product, index) => {
          const price =
            product.selectedOrFirstAvailableVariant.price?.amount || 0;
          const compareAtPrice =
            product.selectedOrFirstAvailableVariant.compareAtPrice?.amount || 0;
          const discountLogic = computeDiscountLogic({
            discountPreview: false,
            onSale: compareAtPrice > price,
            product,
            variant: product.selectedOrFirstAvailableVariant,
          });
          const percentageDiscount = this.getCampaignDiscount(
            discountLogic,
            'active',
          );
          const discountObject = {
            percentageDiscount,
            discountCode: this.discountCode,
          };
          const deliveryTime = this.$string('product.delivery_time', {
            replace: { time: this.$variable('product.deliveryTime') },
          });
          const currencyCode = this.$variable('store.currency');

          return formatTrackingItem({
            index,
            product,
            variant: product.selectedOrFirstAvailableVariant,
            collection: this.collection,
            template: this.liquid.source,
            discountObject,
            deliveryTime,
            currencyCode,
          });
        }),
      });
    },
  },
};
</script>

<style lang="scss">
@import '@/components/splide';
@import './product-carousel';
</style>
